import { useController, useFormContext } from "react-hook-form";
import moment from "moment";
import { Color, DatePicker, SelectMenu } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import { Box, Typography } from "@material-ui/core";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { CollapsibleFavFilter } from "./_base/collapsible-fav-filter";

export interface AvailabilityFilterData {
  from: Date | null;
  to: Date | null;
}

export type AvailabilityFilterProps = {
  label?: string;
} & FilterWithFavouriteProps;

const dateToWeek = (date?: Date | null) => (date ? moment(date).format("v.W") : "");

export const AvailabilityFilter = (props: AvailabilityFilterProps) => {
  const { setValue, resetField, watch } = useFormContext();
  const { fieldState } = useController({ name: props.name });
  const value = watch(props.name) as AvailabilityFilterData;
  const title = props.label ?? locale.Availability;

  // Each picker is bounded by its sibling, so an inverted range cannot be
  // selected. An inverted range yields no weeks at all, which used to reach Mongo
  // as an empty `$search` operand and fail the whole search (CRMDEV-7166).
  const datePickers = (
    <Box display="flex" alignItems="flex-end" gridGap="8px">
      <DatePicker
        fullWidth
        name={`${props.name}.from`}
        isClearable={!!value?.from}
        label={locale.WeekFrom}
        value={value?.from}
        onChange={(v) => {
          setValue(`${props.name}.from`, v, { shouldDirty: true });
        }}
        DatePickerProps={{
          showWeekNumbers: true,
          dateFormat: "w | yyyy-MM-dd",
          maxDate: value?.to ?? undefined
        }}
      />

      <DatePicker
        fullWidth
        name={`${props.name}.to`}
        isClearable={!!value?.to}
        label={locale.WeekTo}
        value={value?.to}
        onChange={(v) => {
          setValue(`${props.name}.to`, v, { shouldDirty: true });
        }}
        DatePickerProps={{
          showWeekNumbers: true,
          dateFormat: "w | yyyy-MM-dd",
          minDate: value?.from ?? undefined
        }}
      />
    </Box>
  );

  if (props.setFavorite) {
    return (
      <CollapsibleFavFilter
        title={title}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        isClearable={fieldState.isDirty}
        onClear={() => resetField(props.name)}
        isDirty={fieldState.isDirty}
        initialExpanded={fieldState.isDirty}
      >
        <Box mr={2}>{datePickers}</Box>
      </CollapsibleFavFilter>
    );
  }

  return (
    <SelectMenu
      badgeLabel={
        !!value?.from || !!value?.to
          ? `${dateToWeek(value?.from)} - ${dateToWeek(value?.to)}`
          : undefined
      }
      placeholder={
        <Typography
          variant="body1"
          style={{ color: Color.DetailsInput, lineHeight: "calc(1rem + 4px)" }}
        >
          {title}
        </Typography>
      }
      openByDefault={false}
      showPlaceholderOnExpand={true}
      isClearable={!!value?.from || !!value?.to}
      onClear={() => resetField(props.name)}
      content={datePickers}
    />
  );
};
